import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Users, 
  Plus, 
  ArrowLeft,
  Lock,
  Globe,
  UserPlus,
  Loader2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ISSUE_AREAS = [
  { id: 'environmental_justice', en: 'Environmental Justice', es: 'Justicia Ambiental' },
  { id: 'voting_rights', en: 'Voting Rights', es: 'Derechos de Voto' },
  { id: 'housing', en: 'Housing', es: 'Vivienda' },
  { id: 'climate_justice', en: 'Climate Justice', es: 'Justicia Climática' },
  { id: 'workers_rights', en: 'Workers\' Rights', es: 'Derechos Laborales' },
];

const Groups = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [filters, setFilters] = useState({ issue_areas: '', location: '' });
  
  const [newGroup, setNewGroup] = useState({
    name: '',
    description: '',
    privacy: 'request',
    issue_areas: [],
    location: ''
  });

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const token = localStorage.getItem('polibot_token');
      const params = new URLSearchParams();
      if (filters.issue_areas) params.append('issue_areas', filters.issue_areas);
      if (filters.location) params.append('location', filters.location);
      
      const response = await axios.get(`${API}/community/groups?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroups(response.data.groups || []);
    } catch (error) {
      toast.error(language === 'es' ? 'Error al cargar grupos' : 'Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setLoading(true);
    fetchGroups();
  };

  const handleCreateGroup = async () => {
    if (!newGroup.name || !newGroup.description) {
      toast.error(language === 'es' ? 'Completa todos los campos' : 'Fill all required fields');
      return;
    }

    setCreating(true);
    try {
      const token = localStorage.getItem('polibot_token');
      await axios.post(`${API}/community/groups`, newGroup, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'es' ? '¡Grupo creado!' : 'Group created!');
      setCreateDialog(false);
      setNewGroup({ name: '', description: '', privacy: 'request', issue_areas: [], location: '' });
      fetchGroups();
    } catch (error) {
      toast.error(language === 'es' ? 'Error al crear grupo' : 'Failed to create group');
    } finally {
      setCreating(false);
    }
  };

  const handleJoin = async (groupId, privacy) => {
    try {
      const token = localStorage.getItem('polibot_token');
      const response = await axios.post(`${API}/community/groups/${groupId}/join`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(
        response.data.status === 'joined' 
          ? (language === 'es' ? '¡Te has unido!' : 'You joined!')
          : (language === 'es' ? 'Solicitud enviada' : 'Request sent')
      );
      fetchGroups();
    } catch (error) {
      const msg = error.response?.data?.detail || (language === 'es' ? 'Error' : 'Failed');
      toast.error(msg);
    }
  };

  const getPrivacyIcon = (privacy) => {
    switch (privacy) {
      case 'open': return <Globe className="h-4 w-4" />;
      case 'request': return <UserPlus className="h-4 w-4" />;
      default: return <Lock className="h-4 w-4" />;
    }
  };

  const getPrivacyLabel = (privacy) => {
    const labels = {
      open: language === 'es' ? 'Abierto' : 'Open',
      request: language === 'es' ? 'Solicitar' : 'Request to Join',
      invite_only: language === 'es' ? 'Solo Invitación' : 'Invite Only',
      hidden: language === 'es' ? 'Oculto' : 'Hidden'
    };
    return labels[privacy] || privacy;
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="groups-loading">
        <Skeleton className="h-10 w-64" />
        <div className="grid md:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-48" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="groups-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/community')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
              {language === 'es' ? 'Grupos' : 'Groups'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {language === 'es' 
                ? 'Únete a coaliciones y campañas'
                : 'Join coalitions and campaigns'}
            </p>
          </div>
        </div>
        <Button onClick={() => setCreateDialog(true)} data-testid="create-group-btn">
          <Plus className="h-4 w-4 mr-2" />
          {language === 'es' ? 'Crear Grupo' : 'Create Group'}
        </Button>
      </div>

      {/* Filters */}
      <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
        <Select 
          value={filters.issue_areas || "all"} 
          onValueChange={(v) => setFilters(prev => ({ ...prev, issue_areas: v === "all" ? "" : v }))}
        >
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder={language === 'es' ? 'Área de Interés' : 'Issue Area'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{language === 'es' ? 'Todas' : 'All'}</SelectItem>
            {ISSUE_AREAS.map(issue => (
              <SelectItem key={issue.id} value={issue.id}>
                {language === 'es' ? issue.es : issue.en}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Input
          placeholder={language === 'es' ? 'Ubicación' : 'Location'}
          value={filters.location}
          onChange={(e) => setFilters(prev => ({ ...prev, location: e.target.value }))}
          className="flex-1"
        />
        
        <Button type="submit">
          {language === 'es' ? 'Buscar' : 'Search'}
        </Button>
      </form>

      {/* Groups Grid */}
      {groups.length > 0 ? (
        <div className="grid md:grid-cols-2 gap-4">
          {groups.map((group, index) => (
            <Card key={group.id} className="hover:border-primary/30 transition-colors" data-testid={`group-${index}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{group.name}</CardTitle>
                    {group.location && (
                      <CardDescription>{group.location}</CardDescription>
                    )}
                  </div>
                  <Badge variant="secondary" className="flex items-center gap-1">
                    {getPrivacyIcon(group.privacy)}
                    {getPrivacyLabel(group.privacy)}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                  {group.description}
                </p>
                
                {group.issue_areas && group.issue_areas.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-4">
                    {group.issue_areas.slice(0, 3).map((issue, i) => (
                      <Badge key={i} variant="outline" className="text-xs">
                        {issue.replace('_', ' ')}
                      </Badge>
                    ))}
                  </div>
                )}
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    {group.member_count_range} {language === 'es' ? 'miembros' : 'members'}
                  </span>
                  
                  {group.is_member ? (
                    <Badge className="bg-success/20 text-success">
                      {group.is_admin 
                        ? (language === 'es' ? 'Admin' : 'Admin')
                        : (language === 'es' ? 'Miembro' : 'Member')}
                    </Badge>
                  ) : (
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleJoin(group.id, group.privacy)}
                    >
                      {group.privacy === 'open' 
                        ? (language === 'es' ? 'Unirse' : 'Join')
                        : (language === 'es' ? 'Solicitar' : 'Request')}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="font-medium text-lg mb-2">
              {language === 'es' ? 'No hay grupos' : 'No groups found'}
            </h3>
            <p className="text-muted-foreground mb-4">
              {language === 'es' 
                ? '¡Sé el primero en crear un grupo!'
                : 'Be the first to create a group!'}
            </p>
            <Button onClick={() => setCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              {language === 'es' ? 'Crear Grupo' : 'Create Group'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Create Group Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {language === 'es' ? 'Crear Nuevo Grupo' : 'Create New Group'}
            </DialogTitle>
            <DialogDescription>
              {language === 'es' 
                ? 'Crea un espacio para colaborar con otros defensores'
                : 'Create a space to collaborate with other advocates'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{language === 'es' ? 'Nombre del Grupo' : 'Group Name'} *</Label>
              <Input
                value={newGroup.name}
                onChange={(e) => setNewGroup(prev => ({ ...prev, name: e.target.value }))}
                placeholder={language === 'es' ? 'Ej: Coalición Aire Limpio' : 'e.g., Clean Air Coalition'}
              />
            </div>
            
            <div className="space-y-2">
              <Label>{language === 'es' ? 'Descripción' : 'Description'} *</Label>
              <Textarea
                value={newGroup.description}
                onChange={(e) => setNewGroup(prev => ({ ...prev, description: e.target.value }))}
                placeholder={language === 'es' ? 'Describe el propósito del grupo...' : 'Describe the group purpose...'}
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <Label>{language === 'es' ? 'Privacidad' : 'Privacy'}</Label>
              <Select 
                value={newGroup.privacy} 
                onValueChange={(v) => setNewGroup(prev => ({ ...prev, privacy: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4" />
                      {language === 'es' ? 'Abierto - Cualquiera puede unirse' : 'Open - Anyone can join'}
                    </div>
                  </SelectItem>
                  <SelectItem value="request">
                    <div className="flex items-center gap-2">
                      <UserPlus className="h-4 w-4" />
                      {language === 'es' ? 'Solicitar - Requiere aprobación' : 'Request - Requires approval'}
                    </div>
                  </SelectItem>
                  <SelectItem value="invite_only">
                    <div className="flex items-center gap-2">
                      <Lock className="h-4 w-4" />
                      {language === 'es' ? 'Solo Invitación' : 'Invite Only'}
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>{language === 'es' ? 'Ubicación (opcional)' : 'Location (optional)'}</Label>
              <Input
                value={newGroup.location}
                onChange={(e) => setNewGroup(prev => ({ ...prev, location: e.target.value }))}
                placeholder={language === 'es' ? 'Ej: Brooklyn, NY' : 'e.g., Brooklyn, NY'}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>
              {language === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button onClick={handleCreateGroup} disabled={creating}>
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              {language === 'es' ? 'Crear' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Groups;
